import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Handshake,
  Users,
  Landmark,
  Network,
  PlaneTakeoff,
  BarChart3,
  TrendingUp,
  Globe,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import PageBanner from "../components/PageBanner";
import Reveal from "../components/Reveal";
import { services, ecosystem, BOOK_MEETING_URL } from "../mock";

const iconMap = { Handshake, Users, Landmark, Network, PlaneTakeoff, BarChart3, TrendingUp };

const categories = [
  "All",
  "Market Entry",
  "Expansion & Advisory",
  "Government & Trade",
];

const ServicesPage = () => {
  const [filter, setFilter] = useState("All");
  const visible =
    filter === "All" ? services : services.filter((s) => s.category === filter);

  return (
    <>
      <PageBanner
        title="Our Services"
        subtitle="Focused consulting services that turn cross-border ambition into results — for international companies, governments and Indian enterprises."
        crumbs={[{ label: "Services" }]}
      />

      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          {/* Filter pills */}
          <Reveal className="mb-10 flex flex-wrap gap-2.5">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFilter(c)}
                data-testid={`service-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className={`rounded-full border px-5 py-2 text-sm font-medium transition-all duration-300 ${
                  filter === c
                    ? "border-[#0F2A4A] bg-[#0F2A4A] text-white"
                    : "border-slate-200 text-slate-600 hover:border-[#0F2A4A]/40 hover:text-[#0F2A4A]"
                }`}
              >
                {c}
              </button>
            ))}
          </Reveal>

          {/* Feature cards */}
          <div className="grid grid-cols-1 gap-6">
            {visible.map((s, i) => {
              const Icon = iconMap[s.icon] || Globe;
              return (
                <Reveal key={s.id} delay={(i % 2) * 90}>
                  <div
                    data-testid={`service-card-${s.id}`}
                    className="group relative overflow-hidden rounded-2xl shadow-[0_16px_50px_-28px_rgba(15,42,74,0.6)]"
                  >
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full scale-[1.08] object-cover blur-[3px] transition-transform duration-[900ms] ease-out group-hover:scale-[1.12]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0b2038]/97 via-[#0F2A4A]/93 to-[#0F2A4A]/82" />

                    <div className="relative grid grid-cols-1 gap-8 p-8 md:grid-cols-5 md:p-10">
                      {/* Left: identity + overview + CTA */}
                      <div className="md:col-span-3">
                        <div className="flex items-center gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-white ring-1 ring-white/25 backdrop-blur-md transition-colors duration-300 group-hover:bg-[#E4545F] group-hover:ring-[#E4545F]">
                            <Icon className="h-6 w-6" />
                          </div>
                          <span className="rounded-full border border-white/25 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#F6A9AE]">
                            {s.category}
                          </span>
                        </div>
                        <h3 className="mt-5 text-xl font-semibold leading-snug text-white md:text-2xl">
                          {s.title}
                        </h3>
                        <p className="mt-3 text-[15px] leading-relaxed text-white/80">
                          {s.overview}
                        </p>
                        <Link
                          to={`/services/${s.id}`}
                          data-testid={`service-readmore-${s.id}`}
                          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white transition-all duration-200 hover:gap-3"
                        >
                          Read more <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>

                      {/* Right: frosted-glass "what you get" panel */}
                      <div className="rounded-xl border border-white/20 bg-white/[0.14] p-6 backdrop-blur-lg md:col-span-2">
                        <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">
                          What you get
                        </p>
                        <ul className="space-y-3">
                          {s.whatYouGet.map((w) => (
                            <li
                              key={w}
                              className="flex items-start gap-2.5 text-sm text-white/90"
                            >
                              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#F6A9AE]" />
                              <span>{w}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Ecosystem reference — platforms, not services */}
          <Reveal className="mt-16">
            <div className="rounded-2xl border border-slate-200/80 bg-[#F6F7F9] p-8 md:p-10">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="max-w-2xl">
                  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#B8862F]">
                    Beyond Consulting
                  </span>
                  <h3 className="mt-2 text-xl font-semibold text-[#0F2A4A] md:text-2xl">
                    Powered by our ecosystem
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Alongside our advisory services, Continex operates two
                    specialist digital platforms that amplify every engagement.
                  </p>
                </div>
                <Link
                  to="/#ecosystem"
                  data-testid="services-ecosystem-link"
                  className="group inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-[#E4545F] transition-all duration-200 hover:gap-3"
                >
                  Explore the ecosystem
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {ecosystem.branches.map((b) => (
                  <Link
                    key={b.id}
                    to="/#ecosystem"
                    data-testid={`services-platform-${b.id}`}
                    className="group flex items-start gap-4 rounded-xl border border-slate-200/80 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_44px_-28px_rgba(15,42,74,0.45)]"
                  >
                    <span
                      className="mt-0.5 h-10 w-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: b.color }}
                    />
                    <span className="flex-1">
                      <span className="flex items-center gap-2">
                        <span className="text-base font-semibold text-[#0F2A4A]">
                          {b.name}
                        </span>
                        <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
                          {b.tagline}
                        </span>
                      </span>
                      <span className="mt-1.5 block text-sm leading-relaxed text-slate-600">
                        {b.desc}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Conversion CTA */}
          <Reveal className="mt-14">
            <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-[#0F2A4A] px-8 py-10 md:flex-row md:items-center md:px-12">
              <div className="max-w-2xl">
                <h3 className="text-xl font-semibold text-white md:text-2xl">
                  Ready to expand your business cross-border?
                </h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-white/70">
                  Talk to our senior advisory team to structure your market
                  entry, trade mission or partnership model.
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
                <a
                  href={BOOK_MEETING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-meeting-cta-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E4545F] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#c9434e]"
                >
                  Book a Meeting
                </a>
                <Link
                  to="/contact"
                  data-testid="services-contact-link"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/10"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;
